import React, { useEffect, useLayoutEffect, useRef, useState, type ReactNode } from 'react';

import cn from 'classnames';

import cls from './Editable.module.css';

type EditableProps = {
    maxLength?: number;
    children: ReactNode;
    onSubmit: (nextValue: string) => void | Promise<void>;
    value?: string;
    defaultValue?: string;
    placeholder?: string;
    disabled?: boolean;
    autoFocus?: boolean;
    isError?: boolean;
    className?: string;
    inputClassName?: string;
    textareaClassName?: string;
    multiline?: boolean;
    rows?: number;
    readOnly?: boolean;
    inputProps?: Omit<
        React.InputHTMLAttributes<HTMLInputElement>,
        'value' | 'defaultValue' | 'onChange' | 'onBlur' | 'onKeyDown'
    >;
    submitOnlyIfChanged?: boolean;
    trimOnSubmit?: boolean;
    transform?: (value: string) => string;
};

export const Editable = ({
    children,
    onSubmit,
    value,
    defaultValue = '',
    placeholder,
    disabled,
    autoFocus = false,
    isError = false,
    className,
    inputClassName,
    textareaClassName,
    multiline = false,
    rows = 1,
    inputProps,
    submitOnlyIfChanged = true,
    trimOnSubmit = false,
    transform,
    readOnly,
    maxLength = 255,
}: EditableProps) => {
    const isControlled = value !== undefined;

    const [isEditing, setIsEditing] = useState(autoFocus && !disabled);

    const [draft, setDraft] = useState(isControlled ? value! : defaultValue);

    const startValueRef = useRef(draft);

    const inputRef = useRef<HTMLInputElement | null>(null);
    const textareaRef = useRef<HTMLTextAreaElement | null>(null);
    const submittingRef = useRef(false);

    useEffect(() => {
        if (isControlled) setDraft(value!);
    }, [isControlled, value]);

    const enterEdit = () => {
        if (disabled) return;
        startValueRef.current = isControlled ? value! : draft;
        setDraft(isControlled ? value! : draft);
        setIsEditing(true);
    };

    useLayoutEffect(() => {
        if (!isEditing) return;
        const el = multiline ? textareaRef.current : inputRef.current;
        if (!el) return;
        el.focus();
        el.select();
        if (multiline && el instanceof HTMLTextAreaElement) {
            el.style.height = '0px';
            el.style.height = `${el.scrollHeight}px`;
        }
    }, [isEditing, multiline]);

    useEffect(() => {
        if (!isEditing || !multiline) return;
        const el = textareaRef.current;
        if (!el) return;
        el.style.height = '0px';
        el.style.height = `${el.scrollHeight}px`;
    }, [draft, isEditing, multiline]);

    const exitEdit = () => setIsEditing(false);

    const normalize = (s: string) => (trimOnSubmit ? s.trim() : s);

    const doSubmit = async () => {
        if (submittingRef.current) return;

        const next = normalize(draft);
        const prev = normalize(startValueRef.current);

        if (submitOnlyIfChanged && next === prev) {
            exitEdit();
            return;
        }

        try {
            submittingRef.current = true;
            await onSubmit(next);
        } finally {
            submittingRef.current = false;
            exitEdit();
        }
    };

    const cancel = () => {
        setDraft(startValueRef.current);
        exitEdit();
    };

    const onKeyDown: React.KeyboardEventHandler<HTMLInputElement | HTMLTextAreaElement> = (e) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            void doSubmit();
        }
        if (e.key === 'Escape') {
            e.preventDefault();
            cancel();
        }
    };

    const onBlur: React.FocusEventHandler<HTMLInputElement | HTMLTextAreaElement> = () => {
        void doSubmit();
    };

    const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        setDraft(transform ? transform(e.target.value) : e.target.value);
    };

    if (!isEditing || readOnly) {
        const isEmpty = !draft;
        return (
            <span
                role="button"
                className={cn(
                    className,
                    isEmpty && placeholder && cls.emptyPlaceholder,
                    isEmpty && isError && cls.emptyError
                )}
                tabIndex={disabled ? -1 : 0}
                onClick={enterEdit}
                onKeyDown={(e) => {
                    if (disabled) return;
                    if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        enterEdit();
                    }
                }}
                aria-disabled={disabled ? 'true' : undefined}
                style={{ cursor: disabled ? 'default' : 'text' }}
            >
                {isEmpty && placeholder ? placeholder : children}
            </span>
        );
    }

    if (multiline) {
        return (
            <textarea
                maxLength={maxLength}
                ref={textareaRef}
                className={cn(textareaClassName ?? cls.textarea)}
                value={draft}
                onChange={handleChange}
                onKeyDown={onKeyDown}
                onBlur={onBlur}
                placeholder={placeholder}
                disabled={disabled}
                rows={rows}
            />
        );
    }

    return (
        <input
            ref={inputRef}
            className={cn(cls.input, inputClassName)}
            value={draft}
            onChange={handleChange}
            onKeyDown={onKeyDown}
            onBlur={onBlur}
            placeholder={placeholder}
            disabled={disabled}
            {...inputProps}
        />
    );
};
